import { Loading } from '@brands/Components/LoadingSpinner/Loading';
import { useAppSelector } from '@brands/hooks/useReduxHook';
import { Organization } from '@brands/services/identity/types/UserProfile';
import { selectAuth } from '@brands/store/selectors/auth';
import { getSecondary, getSecondaryColor } from '@brands/Utils/getPrimaryColor';
import {
  Box,
  Card,
  CardContent,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from '@mui/material';
import React, { useCallback, useMemo } from 'react';

type ReadOnlyOrganizationsProps = {
  setSelectedOrgId: React.Dispatch<React.SetStateAction<string>>;
  selectedOrgId: string;
  currentOrganization: Organization | null;
  organizations: Organization[];
};

const ReadOnlyOrganizations = ({
  setSelectedOrgId,
  selectedOrgId,
  currentOrganization,
  organizations,
}: ReadOnlyOrganizationsProps): JSX.Element => {
  const { userInfo } = useAppSelector(selectAuth);
  const userRole = useMemo(() => userInfo?.role.name ?? null, [userInfo]);

  const mapAvailableServices = useCallback(
    (organization: Organization): string[] => {
      const availableServices: string[] = [];
      const matchingCorporation = organization?.professional_corporations?.find(
        (pc) => pc.state === userInfo.address?.state,
      );
      if (!matchingCorporation) return ['-'];
      if (matchingCorporation.video_case_enabled) availableServices.push('Live consultation');
      if (matchingCorporation.phone_case_enabled) availableServices.push('Phone Consultation');
      if (matchingCorporation.async_case_enabled) availableServices.push('Photo review');
      if (matchingCorporation.second_opinion_case_enabled) availableServices.push('Second Opinion');
      return availableServices;
    },
    [userInfo.address?.state],
  );

  if (!currentOrganization) return <Loading dotted isHeightAuto />;

  return (
    <Box className="fs-unmask">
      <Typography variant="h6" gutterBottom className="fs-unmask">
        Organizations
      </Typography>

      <Typography variant="body2" color="text.secondary" mb={2} className="fs-unmask">
        You may belong to multiple organizations, but only one can be active at a time. Select the organization you'd
        like to use for Dentistry.One services here.
      </Typography>

      <FormControl component="fieldset" sx={{ width: '100%' }} className="fs-unmask">
        <RadioGroup
          value={selectedOrgId}
          onChange={(e) => {
            const org = organizations.find((o) => o.id.toString() === e.target.value);
            if (org && userRole === 'Patient') {
              setSelectedOrgId(org.id.toString());
            }
          }}
          className="fs-unmask"
        >
          {organizations.map((org) => (
            <Card
              key={org.id}
              variant="outlined"
              className="fs-unmask"
              sx={{
                mb: 2,
                borderRadius: 2,
                borderColor: selectedOrgId === org.id.toString() ? 'primary.main' : 'grey.300',
              }}
            >
              <CardContent className="fs-unmask">
                <Stack direction="row" alignItems="center" spacing={1} className="fs-unmask">
                  <FormControlLabel
                    value={org.id.toString()}
                    className="fs-unmask"
                    control={
                      <Radio
                        className="fs-unmask"
                        sx={{
                          '&.Mui-checked': {
                            color: getSecondary(),
                          },
                        }}
                      />
                    }
                    label={
                      <Typography variant="subtitle1" fontWeight="bold" className="fs-unmask">
                        {org.name}
                      </Typography>
                    }
                    sx={{ flex: 1 }}
                  />
                </Stack>

                <Typography variant="body2" color="text.secondary" mt={1} className="fs-unmask">
                  {mapAvailableServices(org).join(', ')}
                </Typography>
              </CardContent>
            </Card>
          ))}
        </RadioGroup>
      </FormControl>
    </Box>
  );
};

export default ReadOnlyOrganizations;
